import { useState, useRef, useEffect, useCallback } from "react";
import {
  HumanMembersSection,
  AITeamSection,
  SystemTeamSection,
  EmailInviteModal,
  ConnectYourAiSection,
  MyKeySection,
  SetupChecklist,
  RelayHelperSection,
  WorkspaceSecuritySection,
  useHelperStatus,
} from "./TeamArea";
import type { CreatedKey, KeyInfo } from "./TeamArea";
import { useWorkspaceMembers } from "../hooks";
import { auth } from "../lib/firebase";
import { getAuthHeader } from "../lib/auth";

export { EasyRelaySkillPanel } from "./TeamArea";

interface TeamPageProps {
  workspaceId?: string;
  onOpenControlCenter?: () => void;
  onScrollReady?: (el: HTMLDivElement | null) => void;
}

const JUMPS: Array<[string, string]> = [
  ["My Key", "team-section-my-key"],
  ["Relay Helper", "team-section-helper"],
  ["AI Chat", "team-section-mcp"],
  ["Team", "team-section-human"],
  ["Security", "team-section-security"],
];

interface KeyInfoResponse { exists?: boolean; last4?: string | null; created_at?: string | null; last_used_at?: string | null }

export function TeamPage({ workspaceId = "default", onScrollReady }: TeamPageProps) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const scrollElRef = useRef<HTMLDivElement>(null);
  const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null);
  const [keyInfo, setKeyInfo] = useState<KeyInfo | null>(null);
  const [keyError, setKeyError] = useState(false);
  const helper = useHelperStatus(workspaceId);
  const { members } = useWorkspaceMembers(workspaceId);
  const isLead = members.some((m) => m.user_id === auth.currentUser?.uid && m.role === "human_lead");

  useEffect(() => {
    if (onScrollReady) onScrollReady(scrollElRef.current);
    return () => { if (onScrollReady) onScrollReady(null); };
  }, [onScrollReady]);

  // GET /api/workspace/member-key — status only (exists, last 4, dates), never the key.
  const loadKeyInfo = useCallback(async () => {
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/workspace/member-key", { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: KeyInfoResponse = await res.json();
      setKeyInfo({
        exists: data.exists === true,
        last4: data.last4 ?? null,
        createdAt: data.created_at ?? null,
        lastUsedAt: data.last_used_at ?? null,
      });
      setKeyError(false);
    } catch (err) {
      console.warn("[TeamPage] member-key status", err);
      setKeyError(true);
    }
  }, [workspaceId]);

  useEffect(() => { void loadKeyInfo(); }, [loadKeyInfo]);

  function handleCreated(created: CreatedKey) {
    setCreatedKey(created);
    void loadKeyInfo();
  }

  function handleSaved() {
    setCreatedKey(null);
    void loadKeyInfo();
  }

  // Scroll the page's own container, not the window (no scrollIntoView).
  function jumpTo(id: string) {
    const el = document.getElementById(id);
    const box = scrollElRef.current;
    if (el && box) box.scrollTo?.({ top: el.offsetTop - 12, behavior: "smooth" });
  }

  const keyExists = !!createdKey || keyInfo?.exists === true;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "16px 24px 12px", borderBottom: "1px solid #5FC4C033", flexShrink: 0 }}>
        <div className="sc-title-row">
          <span className="sc-title">Team</span>
          <nav className="tp-jumps" aria-label="Jump to a section">
            {JUMPS.map(([label, id]) => (
              <button key={id} className="tp-jump" onClick={() => jumpTo(id)}>{label}</button>
            ))}
          </nav>
        </div>
      </div>
      <div
        ref={scrollElRef}
        className="sc-scroll"
        style={{ padding: "24px", overflowY: "auto", flex: 1, minHeight: 0, position: "relative" }}
      >
        <div className="tp-wrap">
          <SetupChecklist
            keyExists={keyExists}
            keyJustCreated={!!createdKey}
            helperOnline={helper.online}
            lastHeartbeat={helper.lastHeartbeat}
          />

          <MyKeySection
            workspaceId={workspaceId}
            keyInfo={keyInfo}
            keyError={keyError}
            onRetry={() => { setKeyError(false); void loadKeyInfo(); }}
            created={createdKey}
            onCreated={handleCreated}
            onSaved={handleSaved}
          />

          <RelayHelperSection workspaceId={workspaceId} helper={helper} hasKey={keyExists} />

          <ConnectYourAiSection created={createdKey} keyExists={keyExists} />

          <div className="sc-section-label">Team</div>
          <HumanMembersSection workspaceId={workspaceId} onInviteClick={() => setShowInviteModal(true)} />
          <AITeamSection workspaceId={workspaceId} />
          <SystemTeamSection workspaceId={workspaceId} helper={helper} />

          <WorkspaceSecuritySection workspaceId={workspaceId} isLead={isLead} />
        </div>

        {showInviteModal && (
          <EmailInviteModal workspaceId={workspaceId} onClose={() => setShowInviteModal(false)} />
        )}
      </div>
    </div>
  );
}
